import React, { useState, useRef, forwardRef, useImperativeHandle, useContext, useEffect } from "react";
import { cx, css } from "@emotion/css";
import {
  IColumn,
  IconButton,
  PrimaryButton,
  DetailsList,
  SelectionMode,
  Selection,
  DefaultButton,
  Text
} from "@fluentui/react";

import FileDownload from "js-file-download";
import { attachmentUtil } from "../services/AttachmentUtil";
import { ConvertUTCTimeToLocalTime } from "../services/DateUtil";
import { getAttachmentFileDownload, postAttachment } from "../services/AttachmentClient";
import { IAttachment } from "../schemas/SLAttachment";
import LoadingSpinner from "../baseComponents/LoadingSpinner";
import { IUserProviderState, UserContext } from "../contexts/UserContext";
import { SLLoadingStatus, SLActionStatus } from "../utilities/utilities";
import { SLCreateSLBatchContext } from "../contexts/SLCreateSLBatchContext";
import { ISLEntryResponse } from "./SLCreate/SLCreateSchemas";
import { ISLBatchResponse } from "./SLBatch/SLBatchSchemas";
import { INotificationMessagesRef } from "./NotificationMessages";
import { AxiosError } from "axios";

const styles = css`
  & .entry-attachments-text {
    margin: 0 0 15px 0;
    max-width: 300px;
  }
  & .entry-attachments-form {
    display: inline-block;
    margin: 8px -8px;

    & .entry-attachments-form--button {
      margin: 0 8px;
    }
  }
  & .entry-attachments-delete-inline {
    height: auto;
  }
  & .entry-attachments-download-inline {
    height: auto;
  }
`;

const deleteAttachmentHandler = (
  item: IAttachment,
  _setAttachments: React.Dispatch<React.SetStateAction<IAttachment[]>>
) => {
  return (event: React.MouseEvent<any>) => {
    event.preventDefault();
    if (confirm("Are you sure want to delete the selected document?")) {
      // delete from local state only, user has to confirm by saving entry
      _setAttachments((previousAttachments) => {
        return previousAttachments.filter((attch) => attch.blobName !== item.blobName);
      });
    }
  };
};

interface IRemoveAttachmentHandlerProps {
  disableRemoveAttachments: boolean;
  item: IAttachment;
  _setAttachments: React.Dispatch<React.SetStateAction<IAttachment[]>>;
}

function RemoveAttachmentHandlerComponent({
  disableRemoveAttachments,
  item,
  _setAttachments
}: IRemoveAttachmentHandlerProps) {
  if (disableRemoveAttachments) {
    return null;
  }
  return (
    <IconButton
      onClick={deleteAttachmentHandler(item, _setAttachments)}
      iconProps={{
        iconName: "Delete"
      }}
      title=""
      ariaLabel="Delete Attachment"
      className="entry-attachments-delete-inline"
    />
  );
}

function DownloadAttachmentHandlerComponent(item: IAttachment) {
  const { accessToken } = useContext(UserContext);

  return (
    <IconButton
      onClick={getAttachmentDownloadHandler(item.blobName, item.fileName, accessToken)}
      iconProps={{
        iconName: "Download"
      }}
      title=""
      ariaLabel="Download Attachment"
      className="entry-attachments-download-inline"
    />
  );
}

function AttachedDateComponent(item: any) {
  return <span>{ConvertUTCTimeToLocalTime(item.uploadedOn)}</span>;
}

function FileSizeComponent(item: any) {
  return <span>{attachmentUtil.convertFileSizeToKb(item.fileSize)}</span>;
}

export interface IAttachmentEntryFormRef {
  setAttachments: (theAttachments: IAttachment[]) => void;
  getAttachments: () => IAttachment[];
  reset: () => void;
}

export interface IAttachmentEntryFormProps {
  onNotification: INotificationMessagesRef["setMessage"];
}

const Attachments = forwardRef((props: IAttachmentEntryFormProps, ref: React.Ref<IAttachmentEntryFormRef>) => {
  const { accessToken } = useContext(UserContext);
  const entryContext = useContext(SLCreateSLBatchContext);
  const [loadingState, setLoadingState] = useState(SLLoadingStatus.Idle);

  const [selection, setSelection] = useState(new Selection());
  const [attachments, _setAttachments] = useState<IAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    setAttachments(theAttachments: IAttachment[]) {
      _setAttachments(theAttachments);
    },
    getAttachments() {
      return attachments;
    },
    reset() {
      _setAttachments([]);
      setSelection(new Selection());
    }
  }));

  useEffect(() => {
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (entryContext.entry) {
        if (entryContext.dataType === "SLCreate") {
          _setAttachments((entryContext.entry as ISLEntryResponse).attachments);
        } else if (entryContext.dataType === "SLBatch") {
          _setAttachments((entryContext.entry as ISLBatchResponse).attachmentInfo);
        }
      } else {
        _setAttachments([]);
      }
      setLoadingState(SLLoadingStatus.Resolved);
    } else if (entryContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingState(SLLoadingStatus.Rejected);
    } else {
      setLoadingState(SLLoadingStatus.Pending);
    }
  }, [entryContext.loadingStatus, entryContext.entry]);

  const columns = [
    {
      key: "col1_IsVolatile",
      name: "File Type",
      ariaLabel: "Attachment not saved to JE yet.",
      isIconOnly: true,
      fieldName: "blobName",
      minWidth: 16,
      maxWidth: 16,
      onRender: (item: IAttachment) =>
        RemoveAttachmentHandlerComponent({
          item,
          _setAttachments,
          disableRemoveAttachments:
            entryContext.getPermission("removeAttachment") === SLActionStatus.Disabled || entryContext.isReadOnly
        })
    },
    {
      key: "col2a_Download",
      name: "download",
      ariaLabel: "Download file.",
      isIconOnly: true,
      fieldName: "url",
      minWidth: 16,
      maxWidth: 16,
      onRender: (item: IAttachment) => DownloadAttachmentHandlerComponent(item)
    },
    {
      key: "col2Key_FileName",
      name: "File Name",
      fieldName: "fileName",
      minWidth: 200,
      maxWidth: 500,
      isResizable: true
    },
    {
      key: "col3Key_UploadedOn",
      name: "Attached Date",
      fieldName: "uploadedOn",
      minWidth: 50,
      maxWidth: 200,
      isResizable: true,
      onRender: (item) => AttachedDateComponent(item)
    } as IColumn,
    {
      key: "col4Key_FileSize",
      name: "Size (KB)",
      fieldName: "fileSize",
      minWidth: 10,
      maxWidth: 100,
      isResizable: true,
      onRender: (item) => FileSizeComponent(item)
    } as IColumn
  ];
  const onInputClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement;
    element.value = "";
  };

  const fileSelectedHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = validateFiles(event);
    if (files) {
      for (const file of Array.from(files)) {
        try {
          const attachment = await postAttachment(file, accessToken);
          _setAttachments((previousAttachments) => {
            return [
              ...previousAttachments,
              {
                blobName: attachment.blobName,
                fileName: attachment.inputFileName,
                url: attachment.fileStorageUri,
                fileSize: attachment.fileSize,
                uploadedOn: attachment.uploadedOn
              }
            ];
          });
        } catch (err) {
          const messages = ["Unable to attach file(s)."];
          const error = err as unknown as AxiosError;
          if (error.response && error.response.data) {
            messages.push(error.response.data);
          }
          props.onNotification("errors", messages);
        }
      }
    }
  };
  const addAttachmentLauncher = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const deleteAttachment = () => {
    const attchs = selection.getSelection() as IAttachment[];
    let dialogMessage = "Are you sure want to delete the selected documents?";
    switch (attchs.length) {
      case 0:
        alert("Please select one or more documents to delete.");
        return;
      default:
        dialogMessage = "Are you sure want to delete the selected document?";
        break;
    }
    if (confirm(dialogMessage)) {
      // delete from local state only, user has to confirm by saving entry
      const blobNames = attchs.map((item) => item.blobName);
      _setAttachments((previousAttachments) => {
        return previousAttachments.filter((attch) => !blobNames.includes(attch.blobName));
      });
    }
  };

  if (loadingState === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading attachments" />;
  } else if (loadingState === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingState === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        Error while loading Attachments.
      </Text>
    );
  }
  return (
    <React.Fragment>
      <div className={cx(styles, "ms-Grid")} dir="ltr">
        <DetailsList
          items={attachments}
          columns={columns}
          selection={selection}
          selectionMode={SelectionMode.multiple}
          ariaLabel="Attachment Grid"
          ariaLabelForGrid="Table for displaying attachments"
          ariaLabelForSelectAllCheckbox="Toggle all Attachments"
        />
        <div className="entry-attachments-form">
          <input
            type="file"
            onChange={fileSelectedHandler}
            onClick={onInputClick}
            name="InputFile"
            style={{ display: "none" }}
            ref={fileInputRef}
            accept=".doc, .xls, .ppt, .docx, .xlsx, .pptx, .msg, .png, .txt, .csv"
            multiple={true}
          />
          <PrimaryButton
            style={{
              backgroundColor: "var(--main-bg-color, gray)"
            }}
            text="Add Attachment"
            className="entry-attachments-form--button"
            onClick={addAttachmentLauncher}
            disabled={
              entryContext.getPermission("addAttachment") === SLActionStatus.Disabled || entryContext.isReadOnly
            }
          />
          <DefaultButton
            text="Delete Attachment"
            className="entry-attachments-form--button"
            onClick={deleteAttachment}
            disabled={
              entryContext.getPermission("removeAttachment") === SLActionStatus.Disabled || entryContext.isReadOnly
            }
          />
        </div>
      </div>
    </React.Fragment>
  );
});

export function getAttachmentDownloadHandler(
  blobName: string,
  fileName: string,
  getToken: IUserProviderState["accessToken"]
): (event: React.MouseEvent<any>) => void {
  return async (event: React.MouseEvent<any>) => {
    event.preventDefault();
    const downloadedFilePromise: any = getAttachmentFileDownload(`${blobName}`, `${fileName}`, getToken);
    const response = await downloadedFilePromise;
    FileDownload(response.data, fileName, "application/octet-stream");
  };
}

export function validateFiles(event: React.ChangeEvent<HTMLInputElement>): FileList | undefined {
  if (!event.target.files || event.target.files.length === 0) return;
  const fileList = event.target.files;
  const allowedFileTypes = ".doc, .xls, .ppt, .docx, .xlsx, .pptx, .msg, .png, .txt, .csv";

  if (
    !attachmentUtil.getValidatedFileExtensions(fileList, allowedFileTypes) ||
    !attachmentUtil.getValidatedFileSize(fileList)
  )
    return;

  return fileList;
}

export default Attachments;
